import {
  can,
  type AuthUser,
  type LoginInput,
  type LoginResponse,
  type Permission,
} from '@vmc/shared';
import {
  createContext,
  use,
  useCallback,
  useEffect,
  useMemo,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from 'react';
import { api } from './api';
import { getSession, refreshSession, setSession, subscribeSession } from './session';
import { flushPendingActions } from './feedback';

type AuthStatus = 'loading' | 'authenticated' | 'anonymous';

interface AuthContextValue {
  status: AuthStatus;
  user: AuthUser | null;
  login: (input: LoginInput) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

/** Refresh this long before expiry, so in-flight requests never carry an expired token. */
const REFRESH_LEAD_MS = 60_000;

export function AuthProvider({
  children,
  onLogout,
}: {
  children: ReactNode;
  onLogout?: () => void;
}) {
  const session = useSyncExternalStore(subscribeSession, getSession);
  const [bootstrapped, setBootstrapped] = useState(false);

  // Restore the session from the refresh cookie on page load.
  useEffect(() => {
    refreshSession()
      .catch(() => null)
      .finally(() => setBootstrapped(true));
  }, []);

  // Proactive refresh, rescheduled every time a new token arrives.
  useEffect(() => {
    if (!session) return undefined;
    const delay = Math.max(5_000, session.expiresAt - Date.now() - REFRESH_LEAD_MS);
    const timer = setTimeout(() => {
      // Transient failures are retried by the next request's 401 handling.
      refreshSession().catch(() => null);
    }, delay);
    return () => clearTimeout(timer);
  }, [session]);

  // Clear cached server data whenever the session ends, so the next user never sees it.
  const hadSession = session !== null;
  useEffect(() => {
    if (bootstrapped && !hadSession) onLogout?.();
  }, [bootstrapped, hadSession, onLogout]);

  const login = useCallback(async (input: LoginInput) => {
    setSession(await api.public<LoginResponse>('POST', '/api/auth/login', input));
  }, []);

  const logout = useCallback(async () => {
    // Deletes still inside their undo window go through now, while the session is valid.
    flushPendingActions();
    try {
      await fetch('/api/auth/logout', { method: 'POST', credentials: 'include' });
    } finally {
      setSession(null);
    }
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      status: session ? 'authenticated' : bootstrapped ? 'anonymous' : 'loading',
      user: session?.user ?? null,
      login,
      logout,
    }),
    [session, bootstrapped, login, logout],
  );

  return <AuthContext value={value}>{children}</AuthContext>;
}

export function useAuth(): AuthContextValue {
  const ctx = use(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}

/** The signed-in user. Only call below <RequireAuth>. */
export function useUser(): AuthUser {
  const { user } = useAuth();
  if (!user) throw new Error('useUser called without a signed-in user');
  return user;
}

export function useCan(): (permission: Permission) => boolean {
  const { user } = useAuth();
  return useCallback(
    (permission: Permission) => (user ? can(user.role, permission) : false),
    [user],
  );
}
